import { Download, ImageIcon, Video } from "lucide-react";

import {
  AttachmentType,
  SupportRequestHistory,
} from "@/app/entities/SupportRequest";
import { handleDownload } from "@/app/utils/download";
import {
  formatHistoryDate,
  getHistoryEntryTitle,
} from "@/app/utils/supportRequest";
import { cn } from "@/app/utils/cn";

import { formatFileSize } from "./useSupportRequestDetailSheet";

interface HistoryItemProps {
  item: SupportRequestHistory;
  currentUserId?: string;
  isSupportStaff?: boolean;
  isLast: boolean;
}

const HistoryItem = ({
  item,
  currentUserId,
  isSupportStaff,
  isLast,
}: HistoryItemProps) => {
  const isCurrentUser = !!currentUserId && item.actor?.id === currentUserId;
  const actorLabel = isCurrentUser
    ? "Você"
    : isSupportStaff
      ? item.actor?.name || "Suporte"
      : "Suporte";
  const message = item.message?.trim() || getHistoryEntryTitle(item);

  return (
    <div
      className={cn(
        "flex flex-col gap-1 border-b border-gray-200 py-3",
        isLast && "border-b-0 pb-0",
      )}
    >
      <p className="text-xs font-semibold text-black03">
        {actorLabel}, {formatHistoryDate(item.date)}
      </p>

      <p className="whitespace-break-spaces text-xs font-normal text-gray-500">
        {message}
      </p>

      {!!item.attachments?.length && (
        <div className="mt-1 flex flex-col gap-2">
          {item.attachments.map((attachment) => (
            <button
              key={attachment.url}
              type="button"
              onClick={() =>
                handleDownload({
                  url: attachment.url,
                  name: attachment.name,
                })
              }
              className="flex items-center gap-3 rounded-lg border border-gray-300 bg-transparent p-2 text-left"
            >
              {attachment.type === AttachmentType.VIDEO ? (
                <Video className="h-4 w-4 shrink-0" />
              ) : (
                <ImageIcon className="h-4 w-4 shrink-0" />
              )}
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-xs text-gray-700">
                  {attachment.name}
                </span>
                <span className="text-[10px] text-black04">
                  {formatFileSize(attachment.size)}
                </span>
              </div>
              <Download className="h-3.5 w-3.5 shrink-0 text-gray-400" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

interface SupportRequestHistoryProps {
  history: SupportRequestHistory[];
  currentUserId?: string;
  isSupportStaff?: boolean;
}

export const SupportRequestHistorySection = ({
  history,
  currentUserId,
  isSupportStaff,
}: SupportRequestHistoryProps) => {
  if (!history.length) return null;

  return (
    <div className="flex flex-col">
      {history.map((item, index) => (
        <HistoryItem
          key={`${item.date}-${item.action}-${index}`}
          item={item}
          currentUserId={currentUserId}
          isSupportStaff={isSupportStaff}
          isLast={index === history.length - 1}
        />
      ))}
    </div>
  );
};
